import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  AreaChart,
  Area,
  RadialBarChart,
  RadialBar,
  ScatterChart,
  Scatter,
  Cell,
  XAxis,
  YAxis,
  Legend,
} from 'recharts';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  ChartLegend,
  ChartLegendContent,
  ChartConfig,
} from '../ui/chart';

type ChartType = 'line' | 'bar' | 'pie' | 'area' | 'donut' | 'radialBar' | 'gauge' | 'scatter';

interface ChartWidgetProps {
  title: string;
  description?: string;
  type: ChartType;
  data: any[];
  xKey?: string;
  yKey?: string;
  colors?: string[];
}

export default function ChartWidget({
  title,
  description,
  type,
  data,
  xKey = 'name',
  yKey = 'value',
}: ChartWidgetProps) {
  // Criar config do chart baseado nos dados
  const chartConfig: ChartConfig = {
    value: {
      label: yKey,
    },
  } satisfies ChartConfig;

  const renderChart = () => {
    if (!data || data.length === 0) {
      return (
        <div className="h-64 flex items-center justify-center text-muted-foreground">
          Sem dados para exibir
        </div>
      );
    }
    
    // Calcular o máximo valor para o domínio
    const maxValue = Math.max(...data.map(item => Number(item[yKey]) || 0));
    
    // Para gráficos de pizza/donut, converter valores negativos em positivos
    const processedData = (type === 'pie' || type === 'donut') 
      ? data.map(item => ({ ...item, [yKey]: Math.abs(item[yKey] || 0) }))
      : data;

    switch (type) {
      case 'line':
        return (
          <ChartContainer config={chartConfig} className="w-full h-full">
            <LineChart data={processedData}>
              <XAxis 
                dataKey={xKey}
                tickLine={true}
                axisLine={true}
                tickMargin={8}
                tickFormatter={(value) => value.slice(0, 3)}
              />
              <YAxis
                tickLine={true}
                axisLine={true}
                tickMargin={8}
                domain={[0, maxValue]}
              />
              <ChartTooltip content={<ChartTooltipContent />} />
              <Line
                dataKey={yKey}
                type="monotone"
                stroke="hsl(var(--chart-1))"
                strokeWidth={2}
                dot={false}
              />
            </LineChart>
          </ChartContainer>
        );

      case 'bar':
        return (
          <ChartContainer config={chartConfig} className="w-full h-full">
            <BarChart data={processedData}>
              <XAxis
                dataKey={xKey}
                tickLine={true}
                axisLine={true}
                tickMargin={8}
                tickFormatter={(value) => value.slice(0, 3)}
              />
              <YAxis
                tickLine={true}
                axisLine={true}
                tickMargin={8}
                domain={[0, maxValue]}
              />
              <ChartTooltip content={<ChartTooltipContent />} />
              <Bar dataKey={yKey} fill="hsl(var(--chart-1))" radius={8} />
            </BarChart>
          </ChartContainer>
        );

      case 'pie':
        return (
          <ChartContainer config={chartConfig} className="w-full h-full">
            <PieChart>
              <ChartTooltip content={<ChartTooltipContent hideLabel />} />
              <Pie
                data={processedData}
                dataKey={yKey}
                nameKey={xKey}
                cx="50%"
                cy="50%"
                outerRadius="80%"
              >
                {processedData.map((_entry, index) => (
                  <Cell key={`cell-${index}`} fill={`hsl(var(--chart-${(index % 5) + 1}))`} />
                ))}
              </Pie>
              <ChartLegend content={<ChartLegendContent />} />
            </PieChart>
          </ChartContainer>
        );

      case 'donut':
        return (
          <ChartContainer config={chartConfig} className="w-full h-full">
            <PieChart>
              <ChartTooltip content={<ChartTooltipContent hideLabel />} />
              <Pie
                data={processedData}
                dataKey={yKey}
                nameKey={xKey}
                cx="50%"
                cy="50%"
                innerRadius="60%"
                outerRadius="80%"
              >
                {processedData.map((_entry, index) => (
                  <Cell key={`cell-${index}`} fill={`hsl(var(--chart-${(index % 5) + 1}))`} />
                ))}
              </Pie>
              <ChartLegend content={<ChartLegendContent />} />
            </PieChart>
          </ChartContainer>
        );

      case 'area':
        return (
          <ChartContainer config={chartConfig} className="w-full h-full">
            <AreaChart data={processedData}>
              <defs>
                <linearGradient id="fillArea" x1="0" y1="0" x2="0" y2="1">
                  <stop
                    offset="5%"
                    stopColor="hsl(var(--chart-1))"
                    stopOpacity={0.8}
                  />
                  <stop
                    offset="95%"
                    stopColor="hsl(var(--chart-1))"
                    stopOpacity={0.1}
                  />
                </linearGradient>
              </defs>
              <XAxis
                dataKey={xKey}
                tickLine={true}
                axisLine={true}
                tickMargin={8}
                tickFormatter={(value) => value.slice(0, 3)}
              />
              <YAxis
                tickLine={true}
                axisLine={true}
                tickMargin={8}
                domain={[0, maxValue]}
              />
              <ChartTooltip content={<ChartTooltipContent />} />
              <Area
                dataKey={yKey}
                type="natural"
                fill="url(#fillArea)"
                fillOpacity={1}
                stroke="hsl(var(--chart-1))"
                strokeWidth={2}
              />
            </AreaChart>
          </ChartContainer>
        );

      case 'radialBar':
        // Para RadialBar, vamos calcular o percentual
        const radialData = processedData.map((item, index) => ({
          ...item,
          fill: `hsl(var(--chart-${(index % 5) + 1}))`,
        }));
        
        return (
          <ChartContainer config={chartConfig} className="w-full h-full">
            <RadialBarChart 
              cx="50%" 
              cy="50%" 
              innerRadius="10%" 
              outerRadius="80%" 
              data={radialData}
              startAngle={180}
              endAngle={0}
            >
              <ChartTooltip content={<ChartTooltipContent />} />
              <RadialBar
                label={{ position: 'insideStart', fill: 'hsl(var(--background))' }}
                background
                dataKey={yKey}
              />
              <Legend 
                iconSize={10} 
                layout="vertical" 
                verticalAlign="middle" 
                align="right"
                wrapperStyle={{ paddingLeft: '10px' }}
              />
              <ChartTooltip content={<ChartTooltipContent />} />
            </RadialBarChart>
          </ChartContainer>
        );

      case 'gauge':
        // Gauge é um único valor (primeira entrada do array)
        const gaugeValue = processedData[0]?.[yKey] || 0;
        const gaugeMax = 100; // Pode ser configurável
        const gaugePercent = (gaugeValue / gaugeMax) * 100;
        const gaugeColorIndex = gaugePercent > 75 ? 2 : gaugePercent > 50 ? 3 : gaugePercent > 25 ? 1 : 4;
        
        const gaugeData = [
          { name: 'value', value: gaugeValue, fill: `hsl(var(--chart-${gaugeColorIndex}))` },
        ];
        
        return (
          <ChartContainer config={chartConfig} className="w-full h-full">
            <RadialBarChart 
              cx="50%" 
              cy="70%" 
              innerRadius="80%" 
              outerRadius="100%" 
              data={gaugeData}
              startAngle={180}
              endAngle={0}
            >
              <RadialBar
                background={{ fill: 'hsl(var(--muted))' }}
                dataKey="value"
                cornerRadius={10}
                max={gaugeMax}
              />
              <text
                x="50%"
                y="60%"
                textAnchor="middle"
                dominantBaseline="middle"
                className="text-4xl font-bold fill-foreground"
              >
                {gaugeValue}
              </text>
              <text
                x="50%"
                y="72%"
                textAnchor="middle"
                dominantBaseline="middle"
                className="text-sm fill-muted-foreground"
              >
                {processedData[0]?.[xKey] || 'Medidor'}
              </text>
            </RadialBarChart>
          </ChartContainer>
        );

      case 'scatter':
        return (
          <ChartContainer config={chartConfig} className="w-full h-full">
            <ScatterChart>
              <XAxis 
                dataKey={xKey} 
                type="number" 
                name={xKey}
                tickLine={true}
                axisLine={true}
                tickMargin={8}
              />
              <YAxis 
                dataKey={yKey} 
                type="number" 
                name={yKey}
                tickLine={true}
                axisLine={true}
                tickMargin={8}
                domain={[0, maxValue]}
              />
              <ChartTooltip content={<ChartTooltipContent />} />
              <Scatter 
                name={title} 
                data={processedData} 
                fill="hsl(var(--chart-1))"
              />
            </ScatterChart>
          </ChartContainer>
        );

      default:
        return <div>Tipo de gráfico não suportado</div>;
    }
  };

  return (
    <Card className="h-full flex flex-col">
      <CardHeader className="flex-shrink-0">
        <CardTitle className="truncate">{title}</CardTitle>
        {description && <CardDescription className="truncate">{description}</CardDescription>}
      </CardHeader>
      <CardContent className="flex-1 p-4 min-h-0">
        <div className="w-full h-full">
          {renderChart()}
        </div>
      </CardContent>
    </Card>
  );
}
